"use client";

import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import type { FormData } from "@/components/organisms/forms/sing-up/schema/sing-up-schemas";
import { useLocalStorage } from "@/hooks/useLocalStorage";

interface StepContextProps {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  completedSteps: number[];
  goToStep: (step: number) => void;
  completeStep: (step: number) => void;
  userData: any;
  updateUserData: (
    data: Pick<FormData, "fullName" | "email" | "phoneNumber"> | null,
  ) => void;
  clearCompletedSteps: () => void;
}

const StepContext = createContext<StepContextProps | undefined>(undefined);

export const useStep = () => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error("useStep must be used within a StepProvider");
  }
  return context;
};

export const StepProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState(null);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const [values, setValues] = useLocalStorage(
    "abertura-de-vagas-ongoing-session",
    {
      name: "",
      cargoId: "",
      step: 0,
    },
  );
  const [currentStep, setCurrentStep] = useState(values.step || 1);

  const goToStep = (step: number) => {
    if (step > 1 && !completedSteps.includes(step - 1)) return;
    setCurrentStep(step);
  };

  const completeStep = (step: number) => {
    if (completedSteps.includes(step)) return;
    setCompletedSteps([...completedSteps, step]);

    const newStep = step + 1;
    goToStep(newStep);
    if (newStep <= 3) {
      setValues({ ...values, step: newStep });
    }
  };

  const clearCompletedSteps = useCallback(() => {
    setCurrentStep(1);
    setCompletedSteps([]);
  }, [setCurrentStep, setCompletedSteps]);

  const updateUserData = (data: any) => setUserData(data);

  // Fix completed steps when going back to "confirmar-dados"
  useEffect(() => {
    if (values.step > 1) {
      setCompletedSteps(Array.from({ length: values.step }, (_, i) => i + 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepContext.Provider
      value={{
        currentStep,
        completedSteps,
        goToStep,
        completeStep,
        updateUserData,
        userData,
        setCurrentStep,
        clearCompletedSteps,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};
